import moment from "moment";
import {
    ITrip,
    ITripItem,
    ITripTraveller,
    TripOptionalData,
    IAddItemBase,
    IAddItemToNewQuote,
    IAddItemToExistingQuote,
    IQuote,
    ITripShapeUpdate,
    ITripSummary,
    BookingData,
    ICartItemData,
    IGuestNested,
    TRIP_TITLE_PREFIX,
} from "@interfaces";
import { ISetCustomerData, ICustomerInfo, ICustomerDetail } from "src/Interfaces/customer";
import { eighteenYearsAgoISO } from "src/Utilities/dateOfBirth";

/**
 * An overnight stay is anywhere the traveller sleeps the night:
 *  - any accommodation product (`productClass === "accommodation"`) regardless
 *    of nights — a 1-night hotel can legitimately render as a single-day card;
 *  - OR any item spanning more than one day-column (multi-night stays / other
 *    multi-day items, rendered as a connecting band).
 * Single-day activities / tours are NOT overnight stays. These are the items
 * the calendar marks with a house token.
 */
export const isOvernightStay = (
    item: Pick<ITripItem, "durationDays" | "productClass">,
): boolean => item.productClass === "accommodation" || item.durationDays > 1;

/**
 * Resolve the productClass to persist on a trip item.
 *
 * The accommodation room picker already KNOWS the product is accommodation
 * (the sidebar normalises the raw TDMS class — e.g. the "A" code — to the
 * canonical `"accommodation"`). But the AvailabilityModal re-fetches the TDMS
 * product detail to price the room, and that detail's `productClass` is the
 * raw code (or absent). Persisting the re-fetched class loses the
 * normalisation, so the item never reads as accommodation downstream (no house
 * token, etc.). Prefer the caller-supplied override and only fall back to the
 * fetched class when no override is provided.
 */
export const resolveItemProductClass = (
    override: string | null | undefined,
    fetched: string | null | undefined,
): string | null => override ?? fetched ?? null;

/**
 * Normalise a persisted/raw TDMS product class to the canonical value the
 * Trip Planner keys off. The FT search endpoint and sidebar normalise the
 * short hotel code ("A") to "accommodation"; legacy trip snapshots saved
 * before that normalisation persisted the raw "A". Mirror the sidebar here so
 * re-opened trips classify accommodation consistently (and render the
 * overnight house token).
 */
export const normaliseTripItemClass = (
    raw: string | null | undefined,
): string | null => (raw === "A" ? "accommodation" : raw ?? null);

/**
 * Lightweight UUID generator. Falls back to a Math.random based id if
 * crypto.randomUUID isn't available (older test environments).
 */
export const newTripItemId = (): string => {
    const c: { randomUUID?: () => string } | undefined =
        typeof crypto !== "undefined" ? (crypto as { randomUUID?: () => string }) : undefined;
    if (c && typeof c.randomUUID === "function") {
        return c.randomUUID();
    }
    return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

/** Date for a given dayIndex relative to the trip's startDate, as YYYY-MM-DD. */
export const dateForDayIndex = (startDate: string, dayIndex: number): string =>
    moment(startDate, "YYYY-MM-DD").add(dayIndex, "days").format("YYYY-MM-DD");

/** Total RRP for the trip (rrp * quantity per item). */
export const computeTripTotal = (trip: ITrip): number =>
    trip.items.reduce((sum, item) => sum + (item.rrp || 0) * (item.quantity || 0), 0);

/**
 * Number of travellers an item books. The trip planner's `quantity` IS the
 * actual booking count sent to the cart (e.g. a TWIN room with numPax=2 and
 * qty=2 books two people), so the per-product picker expects this many people
 * selected.
 */
export const travellerCountForItem = (item: Pick<ITripItem, "quantity">): number =>
    Math.max(1, item.quantity || 1);

/**
 * Stable client-generated id for a roster traveller. Falls back to a
 * Math.random based id when crypto.randomUUID isn't available (older test
 * environments). Mirrors `newTripItemId`.
 */
export const newTravellerId = (): string => {
    const c: { randomUUID?: () => string } | undefined =
        typeof crypto !== "undefined" ? (crypto as { randomUUID?: () => string }) : undefined;
    if (c && typeof c.randomUUID === "function") {
        return c.randomUUID();
    }
    return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

/** A blank roster traveller with a fresh id and all fields empty. */
export const emptyTraveller = (): ITripTraveller => ({
    id: newTravellerId(),
    title: "",
    firstName: "",
    lastName: "",
    // Adults are the common case — default to the same date 18 years ago. This
    // is treated as "untouched" by the roster's blank-row cleanup, so a seeded
    // row with only this default is still dropped on save.
    dateOfBirth: eighteenYearsAgoISO(),
    email: "",
    phoneNumber: "",
    postalCode: "",
    countryCode: "",
});

/**
 * Map a backend account-level customer/redeemer record (snake_case, from
 * `GET cart/redeemers/user`) into a roster traveller. Always assigns a fresh
 * client id so importing the same saved person twice never collides, and so the
 * imported copy is decoupled from the backend redeemer id.
 */
export const customerInfoToTraveller = (c: ICustomerInfo): ITripTraveller => ({
    id: newTravellerId(),
    title: c.title ?? "",
    firstName: c.first_name ?? "",
    lastName: c.last_name ?? "",
    dateOfBirth: c.date_of_birth ?? "",
    email: c.email ?? "",
    phoneNumber:
        c.phone_number === null || c.phone_number === undefined
            ? ""
            : String(c.phone_number),
    postalCode: c.postal_code ?? "",
    countryCode: c.country_code ?? "",
});

/**
 * Map an account-level saved traveller ("guest", from `GET cart/customers`) into
 * a roster traveller. Guests are the same people the user manages in the Profile
 * Address Book, so importing one here reuses those saved details. A fresh client
 * id is always assigned (the backend `_id` is dropped) so the same guest can be
 * imported more than once without colliding. Guests have no title field.
 */
export const guestToTraveller = (g: IGuestNested): ITripTraveller => ({
    id: newTravellerId(),
    title: "",
    firstName: g.first_name ?? "",
    lastName: g.last_name ?? "",
    dateOfBirth: g.date_of_birth ?? "",
    email: g.email ?? "",
    phoneNumber:
        g.phone_number === null || g.phone_number === undefined
            ? ""
            : String(g.phone_number),
    postalCode: g.post_code ?? "",
    countryCode: g.country_code ?? "",
});

/**
 * Stable identity key for de-duplicating travellers across the roster and the
 * saved-traveller list. Case-insensitive on name + DOB.
 */
export const travellerIdentityKey = (
    t: Pick<ITripTraveller, "firstName" | "lastName" | "dateOfBirth">,
): string =>
    `${t.firstName ?? ""}|${t.lastName ?? ""}|${t.dateOfBirth ?? ""}`
        .trim()
        .toLowerCase();

/**
 * Pad or trim a roster array to exactly `count` entries, preserving any
 * already-entered people (and their ids). New slots are filled with blank
 * travellers. Pure — returns a new array and never mutates the input.
 */
export const ensureRosterCount = (
    travellers: ITripTraveller[] | undefined,
    count: number,
): ITripTraveller[] => {
    const target = Math.max(0, count);
    const existing = travellers ?? [];
    if (existing.length === target) return existing.map((t) => ({ ...t }));
    if (existing.length > target) return existing.slice(0, target).map((t) => ({ ...t }));
    const next = existing.map((t) => ({ ...t }));
    while (next.length < target) next.push(emptyTraveller());
    return next;
};

/** True when a traveller has the minimum identifying details filled in. */
export const isTravellerComplete = (t: ITripTraveller): boolean =>
    Boolean(t.firstName?.trim() && t.lastName?.trim());

/**
 * Resolve an item's selected `travellerIds` to the roster people, in the
 * order they were selected. Ids that no longer exist on the roster (e.g. the
 * person was removed) are dropped. Pure.
 */
export const selectedTravellersForItem = (
    trip: Pick<ITrip, "travellers">,
    item: Pick<ITripItem, "travellerIds">,
): ITripTraveller[] => {
    const byId = new Map((trip.travellers ?? []).map((t) => [t.id, t]));
    return (item.travellerIds ?? [])
        .map((id) => byId.get(id))
        .filter((t): t is ITripTraveller => Boolean(t));
};

/** Count of roster people currently selected on an item (for the badge). */
export const selectedTravellerCount = (item: Pick<ITripItem, "travellerIds">): number =>
    (item.travellerIds ?? []).length;

/**
 * Whether an item's traveller selection reconciles with its booking quantity.
 * The picker requires exactly `quantity` people selected (the quantity IS the
 * booking count). Pure — used to gate Save and surface validation messaging.
 */
export const isItemTravellerSelectionValid = (
    item: Pick<ITripItem, "travellerIds" | "quantity">,
): boolean => selectedTravellerCount(item) === travellerCountForItem(item);

/** Drop empty/null/undefined values from an optional-data record. Pure. */
export const cleanOptionalData = (
    data: Record<string, string | number> | undefined,
): Record<string, string | number> => {
    const out: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(data ?? {})) {
        if (value !== "" && value !== null && value !== undefined) out[key] = value;
    }
    return out;
};

/**
 * Map a trip traveller to the cart's `ISetCustomerData` shape used by
 * `redeemerApi.AddRedeemer`. `customerIndex` is the 0-based position of the
 * traveller within the item (the cart numbers redeemers per booking).
 */
export const travellerToCustomerData = (
    t: ITripTraveller,
    customerIndex: number,
): ISetCustomerData => ({
    title: t.title || "",
    firstName: t.firstName || "",
    lastName: t.lastName || "",
    dateOfBirth: t.dateOfBirth || "",
    email: t.email || "",
    customerIndex,
    phoneNumber: t.phoneNumber || "",
    postalCode: t.postalCode || "",
    countryCode: t.countryCode || "",
});

/** Items grouped by day index (0..durationDays-1). Empty days yield empty arrays. */
export const groupItemsByDay = (trip: ITrip): ITripItem[][] => {
    const buckets: ITripItem[][] = Array.from({ length: trip.durationDays }, () => []);
    for (const item of trip.items) {
        const idx = Math.max(0, Math.min(trip.durationDays - 1, item.dayIndex));
        buckets[idx].push(item);
    }
    return buckets;
};

/** One item's presence on a single day of the trip. */
export interface DayOccupancy {
    item: ITripItem;
    /** True on the item's first (check-in / departure) day — full detail. */
    isStart: boolean;
    /** True on the item's last (check-out / return) day. */
    isEnd: boolean;
}

/**
 * Items grouped by day index, but with multi-day items (accommodation stays,
 * multi-day tours) appearing on EVERY day they occupy — their start day plus
 * each continuation day through their span — so the itinerary reflects the
 * real trip footprint instead of collapsing a stay onto its check-in day.
 * `isStart` marks the day the item begins (rendered with full detail) versus
 * continuation days (rendered as a muted "continued" line). Spans are clamped
 * to the trip window so an item moved past the trip end still resolves.
 */
export const groupItemSpansByDay = (trip: ITrip): DayOccupancy[][] => {
    const buckets: DayOccupancy[][] = Array.from(
        { length: trip.durationDays },
        () => [],
    );
    for (const item of trip.items) {
        const start = Math.max(0, Math.min(trip.durationDays - 1, item.dayIndex));
        const span = Math.max(1, Math.floor(item.durationDays));
        const end = Math.min(trip.durationDays - 1, Math.max(start, item.dayIndex + span - 1));
        for (let day = start; day <= end; day += 1) {
            buckets[day].push({ item, isStart: day === start, isEnd: day === end });
        }
    }
    return buckets;
};

/**
 * Plain-text rendering of the trip itinerary, day by day. Used as the payload
 * for the "Share" action (navigator.share / clipboard) and as the body of the
 * print view. Pure — derives everything from the trip and so is easy to test.
 */
export const buildItineraryText = (trip: ITrip): string => {
    const lines: string[] = [];
    lines.push(trip.name?.trim() || "My Trip");
    lines.push(
        `${trip.durationDays} day${trip.durationDays === 1 ? "" : "s"} from ${moment(
            trip.startDate,
            "YYYY-MM-DD",
        ).format("ddd, D MMM YYYY")}`,
    );
    lines.push("");

    const days = groupItemSpansByDay(trip);
    days.forEach((occupancies, dayIndex) => {
        const dateLabel = moment(trip.startDate, "YYYY-MM-DD")
            .add(dayIndex, "days")
            .format("ddd, D MMM YYYY");
        lines.push(`Day ${dayIndex + 1} — ${dateLabel}`);
        if (occupancies.length === 0) {
            lines.push("  (no items)");
        } else {
            for (const { item, isStart } of occupancies) {
                if (!isStart) {
                    // Continuation day of a multi-day stay/tour — name only so
                    // the qty/price isn't double-counted across days.
                    lines.push(`  • ${item.name} (continued)`);
                    continue;
                }
                const parts = [item.name];
                if (item.fareName) parts.push(item.fareName);
                parts.push(`x${item.quantity}`);
                const selected = selectedTravellerCount(item);
                if (selected > 0) parts.push(`${selected} traveller${selected === 1 ? "" : "s"}`);
                lines.push(`  • ${parts.join(" · ")}`);
            }
        }
        lines.push("");
    });

    lines.push(`Total: $${computeTripTotal(trip).toFixed(0)}`);
    return lines.join("\n");
};

/** Escape a string for safe interpolation into HTML text/attribute content. */
const escapeHtml = (s: string): string =>
    s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

/**
 * Self-contained HTML document for printing the itinerary in an isolated
 * window. Both the title and body are HTML-escaped so a hostile trip name or
 * item name can't inject markup/script into the print window. Pure & testable.
 */
export const buildItineraryPrintHtml = (trip: ITrip): string => {
    const title = escapeHtml(trip.name?.trim() || "My Trip");
    const body = escapeHtml(buildItineraryText(trip));
    return (
        `<html><head><title>${title}</title></head>` +
        `<body><pre style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; ` +
        `white-space: pre-wrap;">${body}</pre></body></html>`
    );
};

export interface BandSegment {
    item: ITripItem;
    /** 1-based grid column where the band starts in this week. */
    colStart: number;
    /** Number of columns the band spans within this week. */
    colSpan: number;
    /** True if this segment contains the item's true first day. */
    isStart: boolean;
    /** True if this segment contains the item's true last day. */
    isEnd: boolean;
}

/**
 * Compute the spanning band segments for a week of the trip calendar.
 *
 * - `weekFirstCellDayIdx` is the trip-relative dayIndex of the first cell of
 *   the week (column 1). It can be negative when the trip's first day is not
 *   a Sunday (those leading cells are blank padding).
 * - Returns one segment per multi-day item that intersects the week. Items
 *   that span week boundaries produce one segment per week they touch; the
 *   `isStart`/`isEnd` flags indicate which segment owns the rounded edges.
 * - Single-day items (`durationDays <= 1`) are excluded so the calendar can
 *   render them inside their day cell instead of as a band.
 */
export const computeWeekBandSegments = (
    items: ITripItem[],
    weekFirstCellDayIdx: number,
    tripDurationDays: number,
): BandSegment[] => {
    const realWeekStart = Math.max(0, weekFirstCellDayIdx);
    const realWeekEnd = Math.min(tripDurationDays - 1, weekFirstCellDayIdx + 6);
    if (realWeekStart > realWeekEnd) return [];

    return items
        .filter((it) => it.durationDays > 1)
        .filter((it) => {
            // Clip the item's end to the trip end so an item whose
            // durationDays extends past the trip (e.g. moved late by the user)
            // still gets its rounded right edge / remove button on the
            // visible last segment instead of looking like a continuation.
            const effectiveEnd = Math.min(
                it.dayIndex + it.durationDays - 1,
                tripDurationDays - 1,
            );
            return it.dayIndex <= realWeekEnd && effectiveEnd >= realWeekStart;
        })
        .map((it) => {
            const effectiveEnd = Math.min(
                it.dayIndex + it.durationDays - 1,
                tripDurationDays - 1,
            );
            const segStartDay = Math.max(it.dayIndex, realWeekStart);
            const segEndDay = Math.min(effectiveEnd, realWeekEnd);
            return {
                item: it,
                colStart: segStartDay - weekFirstCellDayIdx + 1,
                colSpan: segEndDay - segStartDay + 1,
                isStart: segStartDay === it.dayIndex,
                isEnd: segEndDay === effectiveEnd,
            };
        })
        .sort((a, b) => a.colStart - b.colStart);
};

/** True if any item is flagged unavailable. */
export const hasUnavailableItems = (trip: ITrip): boolean =>
    trip.items.some((i) => i.availabilityStatus === "unavailable");

/**
 * Snap quantity UP to the nearest multiple of numPax (minimum one step).
 * Always rounds up so a TWIN room (numPax=2) booked for 3 people becomes 4
 * — never silently downgrades a user's intent. Used by addItem's default
 * quantity logic and by TripItemEditModal's manual qty edits, which must
 * agree with each other.
 */
export const snapQuantityToNumPax = (qty: number, numPax: number): number => {
    const step = Math.max(1, numPax || 1);
    return Math.max(step, Math.ceil(qty / step) * step);
};

/**
 * Format a YYYY-MM-DD date as the `DD-MMM-YYYY` form the FT cart/quote
 * endpoints validate against (e.g. `15-May-2026`). Sending raw ISO dates
 * triggers a generic "Validation Error." from the backend — every working
 * callsite (AllFaresAvailabilityPanel, checkoutHelpers, ProductAvailabilities)
 * normalises to this format before posting.
 */
const toApiDate = (isoDate: string): string =>
    moment(isoDate, "YYYY-MM-DD").format("DD-MMM-YYYY");

/** Convert a single trip item to the cart/quote payload shape. */
export const tripItemToAddPayload = (item: ITripItem, startDate: string): IAddItemBase => {
    const bookingDate = toApiDate(dateForDayIndex(startDate, item.dayIndex));
    // Server validation requires a non-empty `timeId`. Products without a
    // specific time slot use the sentinel `"0"` (same convention used by
    // `checkTripItemAvailability` and by the FT cart flow). Sending an empty
    // string triggers a 400 "timeId field is required" from /quotes/new/v2
    // and /cart/items/v2.
    const timeIdStr = item.bookingTimeId ? String(item.bookingTimeId) : "0";
    // Mirror the field set used by the proven cart/quote callsites
    // (`AllFaresAvailabilityPanel.buildCombinedAddItemData`,
    // `transformCartItemsForQuote`). Sending the trip planner's previous
    // top-level `fareTypeId` triggered backend "Validation Error." because
    // sidebar items often don't carry one — `String(undefined)` produced
    // the literal `"undefined"`. Only include `fareTypeId`/`timeId` when
    // they actually have values.
    // Booking keys (defaultRateCode etc.) — only emitted for accommodation
    // fares that actually carry them, so existing tour/activity payloads are
    // byte-for-byte unchanged (avoids the backend's "unexpected field" 400s).
    const bookingKeys: Partial<
        Pick<
            (typeof item),
            "defaultRateCode" | "roomCode" | "externalId" | "externalSource"
        >
    > = {};
    if (item.defaultRateCode) bookingKeys.defaultRateCode = item.defaultRateCode;
    if (item.roomCode) bookingKeys.roomCode = item.roomCode;
    if (item.externalId) bookingKeys.externalId = item.externalId;
    if (item.externalSource) bookingKeys.externalSource = item.externalSource;

    const payload: IAddItemBase = {
        tdmsProductId: String(item.tdmsProductId),
        productPricesDetails: [
            {
                productPricesDetailsId: String(item.productPricesDetailsId),
                quantityDetails: [
                    {
                        quantity: item.quantity,
                        bookingDate,
                        timeId: timeIdStr,
                    },
                ],
                ...bookingKeys,
            },
        ],
        startDate: bookingDate,
        days: Math.max(1, item.durationDays),
        // `selectedAvailableIndices` must point at the row in the most recent
        // availability response that matches this booking. `checkTripItemAvailability`
        // captures the matching index; fall back to 0 only when no check has
        // run yet (the UI gates conversion on availabilityStatus !== "unknown",
        // so in practice we always have a real value here).
        selectedAvailableIndices: [
            typeof item.availIndex === "number" && item.availIndex >= 0 ? item.availIndex : 0,
        ],
    };
    if (item.bookingTimeId) {
        payload.timeId = String(item.bookingTimeId);
    }
    if (item.fareTypeId) {
        payload.fareTypeId = String(item.fareTypeId);
    }
    return payload;
};

/** Build the payload for creating a new quote from the trip's first item. */
export const tripToNewQuotePayload = (trip: ITrip): IAddItemToNewQuote | null => {
    const first = trip.items[0];
    if (!first) return null;
    return {
        ...tripItemToAddPayload(first, trip.startDate),
        quoteTitle: `${TRIP_TITLE_PREFIX}${trip.name}`,
    };
};

/**
 * Read the id of the quote created by `POST /quotes/new/v2` from its response
 * body. The backend returns the created quote in `data[0]` with the id under
 * `quote_id` (e.g. `{ data: [{ quote_id: 42, ... }] }`). We also accept a few
 * defensive single-object / alternate-key shapes so a minor backend change
 * doesn't silently break the save. Returns undefined if no usable id is found.
 */
export const extractNewQuoteId = (body: unknown): string | number | undefined => {
    const pick = (v: unknown): string | number | undefined =>
        v !== undefined && v !== null && String(v) !== "" ? (v as string | number) : undefined;
    if (!body || typeof body !== "object") return undefined;
    const b = body as Record<string, any>;
    const data = b.data;
    // Primary contract: the created quote sits in data[0].quote_id.
    if (Array.isArray(data) && data.length > 0) {
        const first = data[0] ?? {};
        const fromArray = pick(first.quote_id) ?? pick(first.quoteId) ?? pick(first.id);
        if (fromArray !== undefined) return fromArray;
    }
    // Defensive fallbacks for single-object envelopes / top-level ids.
    const d = data && typeof data === "object" && !Array.isArray(data) ? data : {};
    return (
        pick(d.quote_id) ??
        pick(d.quoteId) ??
        pick(d.id) ??
        pick(b.quote_id) ??
        pick(b.quoteId) ??
        pick(b.id)
    );
};

/** Build per-item payloads for adding to an existing quote (skip the first). */
export const tripToAddItemPayloads = (
    trip: ITrip,
    quoteId: string,
): IAddItemToExistingQuote[] =>
    trip.items.slice(1).map((item) => ({
        ...tripItemToAddPayload(item, trip.startDate),
        quoteId,
    }));

/** Cart payloads for every item in the trip. */
export const tripToCartPayloads = (trip: ITrip): IAddItemBase[] =>
    trip.items.map((item) => tripItemToAddPayload(item, trip.startDate));

/**
 * Reverse the trip→quote mapping: rebuild an ITrip (planner state) from a
 * persisted quote so a saved trip can be re-opened on `/trip-planner/:tripId`.
 *
 * - Trip name = title minus `[Trip] ` prefix.
 * - Trip startDate = earliest item start_date.
 * - Trip durationDays = span from earliest start to latest end (clamped 1..40).
 * - Each item's dayIndex = days between its start and the trip start.
 * - Fare metadata (numPax, rrp, fareName, fareTypeId) is recovered by
 *   matching `product_price_details_id` against `product.json.faresPrices`
 *   (with a fallback to the first fare so totally-stale snapshots still load).
 *
 * Items come back with `availabilityStatus = "unknown"` so the planner's
 * standard auto-check effect re-validates each item against current
 * availability — a quote saved months ago shouldn't be silently treated as
 * still bookable.
 */
/**
 * The backend sometimes normalises titles (strips brackets, collapses spaces,
 * lowercases), so a trip we saved as "[Trip] Bali" may come back as
 * "Trip Bali", "trip bali", "[trip] bali", etc. Recognise any of these as a
 * trip title and return the user-visible name with the prefix removed.
 *
 * Returns null if the title is NOT a trip-planner quote (so My Trips can
 * filter it out).
 */
export const stripTripPrefix = (rawTitle: string | undefined | null): string | null => {
    if (typeof rawTitle !== "string") return null;
    // Match leading "[trip]" or "trip" (any case), followed by any whitespace.
    // The brackets are optional because some backends strip them.
    const m = rawTitle.match(/^\s*\[?\s*trip\s*\]?\s+(.*)$/i);
    if (m) return m[1].trim() || "My Trip";
    return null;
};

// ---- Full-fidelity persistence (save → restore exactly) --------------------
//
// A saved trip is an FT quote. Three things the item rows alone can't express
// are persisted/restored here (see the backend contract on `ITripShapeUpdate`
// and `quoteApi.updateTripShape`):
//   1. Trip SHAPE  (start date + full day count, incl. empty days) → the
//      quote's flat `trip_start_date` / `trip_duration` fields.
//   2. ROSTER       (the people on the trip)                        → quote
//      customers via `cart/redeemers?quoteId=`.
//   3. Per-item TRAVELLER ticks + optional answers                  → each
//      item's `booking_data` (redeemer id + optionalData per slot).
// Every reader falls back to the legacy derive-from-items behaviour when a
// field is absent, so pre-change quotes keep opening.

/**
 * Build the trip-shape write fields for the V2 add-to-quote endpoints.
 * `tripStartDate` is formatted `d-M-Y` (e.g. `01-Jun-2026`) per the backend
 * contract; an invalid date or sub-1 duration is sent as null so the backend
 * leaves that field untouched (partial update) rather than storing garbage.
 */
export const buildTripShapeFields = (
    trip: Pick<ITrip, "startDate" | "durationDays">,
): ITripShapeUpdate => {
    const m = moment(trip.startDate, ["YYYY-MM-DD", moment.ISO_8601], true);
    const duration = Math.floor(Number(trip.durationDays));
    return {
        tripStartDate: m.isValid() ? m.format("DD-MMM-YYYY") : null,
        tripDuration: Number.isFinite(duration) && duration >= 1 ? duration : null,
    };
};

/**
 * Read a persisted trip shape from a quote's flat `trip_start_date` /
 * `trip_duration` fields (snake_case, `d-M-Y` on the wire). Both are nullable;
 * returns null when either is missing/invalid so callers fall back to deriving
 * the shape from item dates (legacy behaviour).
 */
export const readQuoteShape = (
    quote: Pick<IQuote, "trip_start_date" | "trip_duration">,
): { startDate: string; durationDays: number } | null => {
    const rawStart = quote.trip_start_date;
    const rawDuration = quote.trip_duration;
    if (
        rawStart === null ||
        rawStart === undefined ||
        rawDuration === null ||
        rawDuration === undefined
    ) {
        return null;
    }
    const start = typeof rawStart === "string" ? rawStart : "";
    const m = moment(start, ["DD-MMM-YYYY", "D-MMM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
    const duration = Number(rawDuration);
    if (!m.isValid() || !Number.isFinite(duration) || duration < 1) return null;
    return {
        startDate: m.format("YYYY-MM-DD"),
        durationDays: Math.min(40, Math.max(1, Math.floor(duration))),
    };
};

/**
 * A persisted customer's stable id. The redeemer id (`id`) is preferred; some
 * payloads only carry the numeric `redeemer_id`. Returns "" when neither is
 * present so the customer is skipped rather than given a bogus id.
 */
export const customerStableId = (
    c: Pick<ICustomerDetail, "id" | "redeemer_id">,
): string => {
    if (c.id !== undefined && c.id !== null && String(c.id) !== "") return String(c.id);
    if (c.redeemer_id !== undefined && c.redeemer_id !== null) return String(c.redeemer_id);
    return "";
};

/**
 * Map the trip roster to the `cart/redeemers` customer payload. `customer_index`
 * preserves roster order so the list round-trips deterministically and per-item
 * references can be re-linked by index on read.
 */
export const rosterToCustomerInfos = (roster: ITripTraveller[]): ICustomerInfo[] =>
    (roster ?? []).map((t, i) => ({
        title: t.title || "",
        customer_index: i,
        first_name: t.firstName || "",
        last_name: t.lastName || "",
        date_of_birth: t.dateOfBirth || "",
        // ICustomerInfo types phone as a number; trips keep the raw string.
        // Cast through unknown so a non-numeric phone survives the round trip.
        phone_number: (t.phoneNumber || "") as unknown as number,
        email: t.email || "",
        postal_code: t.postalCode || "",
        country_code: t.countryCode || "",
    }));

/**
 * Rebuild the trip roster from persisted quote customers (GET cart/redeemers).
 * Each person's id is the backend redeemer id so per-item
 * `booking_data.redeemers` references resolve back to roster people. Ordered by
 * `customer_index` so the roster returns in the saved order.
 */
export const customersToRoster = (
    customers: ICustomerDetail[] | undefined,
): ITripTraveller[] => {
    const sorted = [...(customers ?? [])].sort(
        (a, b) => (Number(a.customer_index) || 0) - (Number(b.customer_index) || 0),
    );
    return sorted
        .map((c): ITripTraveller | null => {
            const id = customerStableId(c);
            if (!id) return null;
            return {
                id,
                title: c.title || "",
                firstName: c.first_name || "",
                lastName: c.last_name || "",
                dateOfBirth: c.date_of_birth || "",
                email: c.email || "",
                phoneNumber:
                    c.phone_number !== undefined && c.phone_number !== null
                        ? String(c.phone_number)
                        : "",
                postalCode: c.postal_code || "",
                countryCode: c.country_code || "",
            };
        })
        .filter((t): t is ITripTraveller => t !== null);
};

/** Map any persisted redeemer id (`id` or `redeemer_id`) to the roster id. */
const buildRedeemerIdToRosterId = (
    customers: ICustomerDetail[] | undefined,
): Map<string, string> => {
    const map = new Map<string, string>();
    for (const c of customers ?? []) {
        const stable = customerStableId(c);
        if (!stable) continue;
        if (c.id !== undefined && c.id !== null) map.set(String(c.id), stable);
        if (c.redeemer_id !== undefined && c.redeemer_id !== null) {
            map.set(String(c.redeemer_id), stable);
        }
    }
    return map;
};

/**
 * Reconstruct an item's selected traveller ids + per-traveller optional answers
 * from its persisted `booking_data` slots, mapping each slot's redeemer id back
 * to a roster traveller id. Slots whose redeemer isn't on the roster are
 * skipped. Pure — exported for testing.
 */
export const bookingDataToTravellers = (
    bookingData: BookingData[] | undefined,
    redeemerIdToRosterId: Map<string, string>,
): { travellerIds: string[]; travellerOptionalData: Record<string, TripOptionalData> } => {
    const travellerIds: string[] = [];
    const travellerOptionalData: Record<string, TripOptionalData> = {};
    for (const bd of bookingData ?? []) {
        const redeemers = Array.isArray(bd.redeemers) ? bd.redeemers : [];
        const rid = redeemers.length
            ? redeemerIdToRosterId.get(String(redeemers[0]))
            : undefined;
        if (!rid) continue;
        if (!travellerIds.includes(rid)) travellerIds.push(rid);
        const cleaned = cleanOptionalData(
            bd.optionalData as Record<string, string | number> | undefined,
        );
        if (Object.keys(cleaned).length) travellerOptionalData[rid] = cleaned;
    }
    return { travellerIds, travellerOptionalData };
};

/**
 * Build the `booking_data` rows that persist an item's per-traveller info on a
 * saved quote: one slot per booked unit, carrying the selected traveller's
 * redeemer id and that traveller's per-product optional answers. Preserves any
 * pre-existing slot fields (pickup/time) so a re-save doesn't wipe them.
 */
export const buildItemBookingData = (
    item: Pick<ITripItem, "quantity" | "bookingTimeId" | "travellerOptionalData">,
    selectedTravellers: ITripTraveller[],
    redeemerIdByTravellerId: Map<string, number>,
    existing: BookingData[] = [],
): BookingData[] => {
    const slotCount = Math.max(item.quantity, selectedTravellers.length, 1);
    const fallbackTimeId =
        item.bookingTimeId === null || item.bookingTimeId === undefined
            ? undefined
            : String(item.bookingTimeId);
    return Array.from({ length: slotCount }, (_unused, i) => {
        const base = (existing[i] ?? {}) as BookingData;
        const baseRedeemers = Array.isArray(base.redeemers) ? base.redeemers : [];
        const t = selectedTravellers[i];
        const redeemerId = t ? redeemerIdByTravellerId.get(t.id) : undefined;
        const optionalData = t ? cleanOptionalData(item.travellerOptionalData?.[t.id]) : {};
        return {
            ...base,
            quantityIndex: base.quantityIndex ?? i,
            timeId: base.timeId ?? fallbackTimeId,
            // A slot with no traveller (deselection) clears its redeemers; a
            // mapped traveller wins; an unmapped-but-present traveller keeps the
            // existing redeemers as a best-effort fallback.
            redeemers: t
                ? redeemerId === undefined || redeemerId === null
                    ? baseRedeemers
                    : [redeemerId]
                : [],
            // Replace (not merge) so answers the user cleared are truly removed
            // — merging would resurrect stale optional data on the next reopen.
            optionalData,
        } as BookingData;
    });
};

/**
 * Pair each trip item with the persisted quote item that represents it, matched
 * by (productPricesDetailsId + start date), first-match-wins so duplicate
 * products on the same day map 1:1. Used on save to know which quote item to
 * write each item's traveller `booking_data` onto. Pure — exported for testing.
 */
export const pairTripItemsToQuoteItems = (
    trip: Pick<ITrip, "items" | "startDate">,
    quoteItems: ICartItemData[],
): Array<{ item: ITripItem; quoteItem: ICartItemData }> => {
    const used = new Set<number>();
    const pairs: Array<{ item: ITripItem; quoteItem: ICartItemData }> = [];
    for (const item of trip.items) {
        const wantedDate = dateForDayIndex(trip.startDate, item.dayIndex);
        const wantedPpdid = String(item.productPricesDetailsId);
        const match = quoteItems.find(
            (q) =>
                !used.has(q.id) &&
                String(q.product_price_details_id) === wantedPpdid &&
                moment(q.start_date, [moment.ISO_8601, "YYYY-MM-DD", "DD-MMM-YYYY"]).format(
                    "YYYY-MM-DD",
                ) === wantedDate,
        );
        if (match) {
            used.add(match.id);
            pairs.push({ item, quoteItem: match });
        }
    }
    return pairs;
};

export const quoteToTrip = (quote: IQuote, customers?: ICustomerDetail[]): ITrip => {
    const rawTitle = typeof quote.title === "string" ? quote.title : "";
    const stripped = stripTripPrefix(rawTitle);
    const name = stripped ?? (rawTitle || "My Trip");
    const items = quote.items || [];
    // Persisted shape + roster (absent on legacy quotes → null / []).
    const shape = readQuoteShape(quote);
    const roster = customersToRoster(customers);
    const redeemerMap = buildRedeemerIdToRosterId(customers);
    if (items.length === 0) {
        return migrateTripTravellers({
            id: quote.id,
            name,
            startDate: shape?.startDate ?? moment().format("YYYY-MM-DD"),
            durationDays: shape?.durationDays ?? 7,
            numTravellers: Math.max(1, roster.length || 2),
            travellers: roster,
            items: [],
        });
    }

    const startMoments = items.map((it) =>
        moment(it.start_date, [moment.ISO_8601, "YYYY-MM-DD", "DD-MMM-YYYY"]).startOf("day"),
    );
    const earliest = startMoments.reduce(
        (acc, m) => (m.isBefore(acc) ? m.clone() : acc),
        startMoments[0].clone(),
    );
    const latestEnd = items.reduce((max, it, idx) => {
        const end = startMoments[idx]
            .clone()
            .add(Math.max(0, (it.days || 1) - 1), "days");
        return end.isAfter(max) ? end : max;
    }, earliest.clone());
    const derivedDuration = Math.max(
        1,
        Math.min(40, latestEnd.diff(earliest, "days") + 1),
    );
    // Prefer the persisted shape (preserves empty leading/trailing days and the
    // exact start date); fall back to the derived span for legacy quotes.
    const tripStart = shape ? moment(shape.startDate, "YYYY-MM-DD").startOf("day") : earliest;
    const durationDays = shape ? shape.durationDays : derivedDuration;

    // Quote items carry the (denormalised) product snapshot in
    // `product.json`. Some legacy snapshots use `faresPrices` (camel-case)
    // instead of the canonical `faresprices`, so we read both.
    type SnapshotFare = {
        productPricesDetailsId?: string | number;
        fareTypeId?: string | number;
        fareName?: string;
        rrp?: number | string;
        numPax?: number | string;
        defaultRateCode?: string;
        roomCode?: string;
        externalId?: string | number;
        externalSource?: string;
    };
    type SnapshotJson = Partial<{
        name: string;
        productImagePath: string;
        productClass: string | null;
        faresprices: SnapshotFare[];
        faresPrices: SnapshotFare[];
    }>;
    const tripItems: ITripItem[] = items.map((it, idx) => {
        const json: SnapshotJson = (it.product?.json as SnapshotJson | undefined) ?? {};
        const fares: SnapshotFare[] = json.faresPrices || json.faresprices || [];
        const fare: SnapshotFare =
            fares.find(
                (f) => String(f?.productPricesDetailsId) === String(it.product_price_details_id),
            ) ||
            fares[0] ||
            {};
        const numPax =
            parseFloat(String(fare.numPax ?? 1).replace(/[^\d.-]/g, "")) || 1;
        const dayIndex = Math.max(
            0,
            Math.min(durationDays - 1, startMoments[idx].diff(tripStart, "days")),
        );
        // Restore per-item traveller ticks + optional answers from booking_data
        // (empty when there's no roster / legacy quote → migrate folds legacy).
        const { travellerIds, travellerOptionalData } = bookingDataToTravellers(
            it.booking_data,
            redeemerMap,
        );
        // Clamp item span so a stale/oversized accommodation can't extend past
        // the (potentially clamped-to-40) trip window.
        const itemSpan = Math.max(
            1,
            Math.min(durationDays - dayIndex, it.days || 1),
        );
        return {
            id: newTripItemId(),
            productId: String(it.tdms_product_id),
            tdmsProductId: Number(it.tdms_product_id) || 0,
            name: json.name || "Item",
            productImagePath: json.productImagePath,
            productClass: normaliseTripItemClass(json.productClass),
            dayIndex,
            durationDays: itemSpan,
            fareTypeId: String(fare.fareTypeId ?? ""),
            fareName: fare.fareName,
            productPricesDetailsId: String(it.product_price_details_id),
            defaultRateCode: fare.defaultRateCode || undefined,
            roomCode: fare.roomCode || undefined,
            externalId:
                fare.externalId !== undefined && fare.externalId !== null
                    ? String(fare.externalId)
                    : undefined,
            externalSource: fare.externalSource || undefined,
            quantity: Math.max(numPax, it.booking_quantity || numPax),
            numPax,
            rrp: Number(fare.rrp) || 0,
            bookingTimeId: it.time_id || null,
            travellerIds,
            travellerOptionalData,
            availabilityStatus: "unknown",
            availabilityVersion: 0,
        };
    });

    // Recover the trip-level traveller count from the largest item.quantity
    // — that's the most likely original "people travelling" value the saver
    // intended (single-pax extras can be lower; nothing should be higher).
    const derivedNumTravellers = Math.max(
        1,
        ...tripItems.map((it) => Math.max(1, Math.floor(it.quantity / Math.max(1, it.numPax)) * 1)),
        ...tripItems.map((it) => it.quantity),
    );
    // Prefer the persisted roster size; fall back to the derived count.
    const numTravellers = roster.length > 0 ? roster.length : derivedNumTravellers;

    return migrateTripTravellers({
        id: quote.id,
        name,
        startDate: tripStart.format("YYYY-MM-DD"),
        durationDays,
        numTravellers,
        travellers: roster,
        items: tripItems,
    });
};

/**
 * A trip item as it may appear in *persisted/legacy* state: before the roster
 * redesign each item carried a full `travellers` array (one record per booked
 * spot, each with its own `optionalData`). New code never writes this shape;
 * it's only read by the one-time migration below.
 */
type LegacyTraveller = Partial<ITripTraveller> & {
    optionalData?: Record<string, string | number>;
};
type LegacyTripItem = ITripItem & { travellers?: LegacyTraveller[] };

/** Dedupe key for folding repeated legacy travellers into a single person. */
const rosterKey = (t: { firstName?: string; lastName?: string; dateOfBirth?: string; email?: string }): string =>
    [t.firstName ?? "", t.lastName ?? "", t.dateOfBirth ?? "", t.email ?? ""]
        .map((s) => s.trim().toLowerCase())
        .join("|");

/**
 * Normalise a trip onto the roster model and fold any legacy per-item
 * `travellers` into the trip-level roster + per-item `travellerIds` /
 * `travellerOptionalData`.
 *
 * - Ensures `trip.travellers` exists and every person has a stable id.
 * - Ensures every item has `travellerIds` and `travellerOptionalData`.
 * - For legacy items: each *complete* legacy traveller becomes a roster person
 *   (deduped across items by name/dob/email), the item references it by id,
 *   and that traveller's `optionalData` is moved under
 *   `item.travellerOptionalData[id]`. Incomplete (blank) legacy slots are
 *   dropped. The legacy `travellers` array is stripped from the item.
 *
 * Pure and idempotent — running it on an already-migrated trip is a no-op
 * (beyond filling in any missing empty arrays). Safe to apply on every load.
 */
export const migrateTripTravellers = (trip: ITrip): ITrip => {
    const roster: ITripTraveller[] = (trip.travellers ?? []).map((t) => ({
        ...t,
        id: t.id || newTravellerId(),
    }));
    const keyToId = new Map<string, string>();
    for (const r of roster) keyToId.set(rosterKey(r), r.id);

    const items = trip.items.map((rawItem) => {
        const { travellers: legacy, ...item } = rawItem as LegacyTripItem;
        const travellerIds: string[] = [...(item.travellerIds ?? [])];
        const optionalData: Record<string, TripOptionalData> = { ...(item.travellerOptionalData ?? {}) };

        for (const lt of legacy ?? []) {
            if (!isTravellerComplete(lt as ITripTraveller)) continue;
            const key = rosterKey(lt);
            let id = keyToId.get(key);
            if (!id) {
                id = newTravellerId();
                keyToId.set(key, id);
                roster.push({
                    id,
                    title: lt.title ?? "",
                    firstName: lt.firstName ?? "",
                    lastName: lt.lastName ?? "",
                    dateOfBirth: lt.dateOfBirth ?? "",
                    email: lt.email ?? "",
                    phoneNumber: lt.phoneNumber ?? "",
                    postalCode: lt.postalCode ?? "",
                    countryCode: lt.countryCode ?? "",
                });
            }
            if (!travellerIds.includes(id)) travellerIds.push(id);
            const cleaned = cleanOptionalData(lt.optionalData);
            if (Object.keys(cleaned).length) optionalData[id] = cleaned;
        }

        return { ...item, travellerIds, travellerOptionalData: optionalData };
    });

    return { ...trip, travellers: roster, items };
};

/**
 * Lightweight per-item shape used by the MyTrips expandable list. Built
 * directly from the denormalised `product.json` snapshot already present
 * on each quote item, so rendering the elaborated trip list requires
 * **zero** additional API calls (no TDMS round-trip).
 */
export interface ITripItemDigest {
    productId: string;
    name: string;
    productImagePath?: string;
    productClass?: string | null;
    /** ISO YYYY-MM-DD. */
    startDate: string;
    /** Span in days (1 for activities, N for accommodation). */
    days: number;
    quantity: number;
    fareName?: string;
    /** Per-unit RRP from the saved snapshot. */
    rrp: number;
    /** rrp * quantity (precomputed for display). */
    lineTotal: number;
}

/**
 * Pull a display-ready item digest out of a saved quote without any
 * network calls. Reads everything from the snapshot that was stored when
 * the trip was saved, so this is safe to run on the My Trips list page
 * regardless of TDMS availability.
 */
export const summariseQuoteItems = (quote: IQuote): ITripItemDigest[] => {
    const items = quote.items || [];
    return items.map((it) => {
        type SnapshotFare = {
            productPricesDetailsId?: string | number;
            fareTypeId?: string | number;
            fareName?: string;
            rrp?: number | string;
        };
        type SnapshotJson = Partial<{
            name: string;
            productImagePath: string;
            productClass: string | null;
            faresprices: SnapshotFare[];
            faresPrices: SnapshotFare[];
        }>;
        const json: SnapshotJson = (it.product?.json as SnapshotJson | undefined) ?? {};
        const fares: SnapshotFare[] = json.faresPrices || json.faresprices || [];
        const fare: SnapshotFare =
            fares.find(
                (f) => String(f?.productPricesDetailsId) === String(it.product_price_details_id),
            ) ||
            fares[0] ||
            {};
        const rrp = Number(fare.rrp) || 0;
        const quantity = Math.max(1, it.booking_quantity || 1);
        // Guard against malformed snapshots — without this, moment returns an
        // "Invalid date" string which would surface verbatim in the UI.
        const parsed = moment(it.start_date, [
            moment.ISO_8601,
            "YYYY-MM-DD",
            "DD-MMM-YYYY",
        ]);
        const startDate = parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
        return {
            productId: String(it.tdms_product_id),
            name: json.name || "Item",
            productImagePath: json.productImagePath,
            productClass: normaliseTripItemClass(json.productClass),
            startDate,
            days: Math.max(1, it.days || 1),
            quantity,
            fareName: fare.fareName,
            rrp,
            lineTotal: rrp * quantity,
        };
    });
};

/**
 * Earliest start_date across a quote's items, as YYYY-MM-DD, or undefined
 * if there are no dated items. Used in the My Trips list so the user can
 * tell at a glance when each trip begins.
 */
export const earliestQuoteStartDate = (quote: IQuote): string | undefined => {
    const items = quote.items || [];
    if (items.length === 0) return undefined;
    const moments = items
        .map((it) =>
            moment(it.start_date, [moment.ISO_8601, "YYYY-MM-DD", "DD-MMM-YYYY"]),
        )
        .filter((m) => m.isValid());
    if (moments.length === 0) return undefined;
    return moments
        .reduce((acc, m) => (m.isBefore(acc) ? m : acc), moments[0])
        .format("YYYY-MM-DD");
};

/**
 * Filter+map the user's quotes down to trip-planner ones, sorted newest first.
 *
 * We use `stripTripPrefix` (instead of a literal `startsWith(TRIP_TITLE_PREFIX)`)
 * so trips whose title was normalised by the backend (brackets stripped, case
 * folded, etc.) still appear in My Trips. Without this, users see an empty
 * list even though the quote was created successfully.
 */
export const quotesToTripSummaries = (quotes: IQuote[]): ITripSummary[] => {
    return quotes
        .map((q) => ({ q, name: stripTripPrefix(q.title) }))
        .filter((x): x is { q: IQuote; name: string } => x.name !== null)
        .map(({ q, name }) => ({
            id: q.id,
            name,
            itemCount: q.items?.length ?? 0,
            totalPrice: Number(q.total_rrp ?? 0),
            raw: q,
        }))
        .sort((a, b) =>
            String(b.raw.created_at || "").localeCompare(String(a.raw.created_at || "")),
        );
};
